// CARO. Casa de estilo — cookie consent.
// Necessary cookies always work; analytics (Google Analytics) is opt-in and
// loads ONLY after the user accepts. The choice is persisted and can be changed.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { setAnalyticsSink } from '@/client/lib/analytics';
import { initGA, gaEvent, disableGA } from '@/client/analytics/ga';

const KEY = 'caro.consent.v1';
const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
// Re-request consent after ~24 months (AEPD guidance on consent validity).
const CONSENT_TTL_MS = 1000 * 60 * 60 * 24 * 365 * 2;

interface Decision {
  analytics: boolean;
  ts: string;
}

interface ConsentValue {
  decided: boolean;
  analytics: boolean;
  bannerOpen: boolean;
  accept: (analytics: boolean) => void;
  reopen: () => void;
}

const ConsentContext = createContext<ConsentValue | null>(null);

function applyAnalytics(granted: boolean) {
  if (granted && GA_ID) {
    initGA(GA_ID);
    setAnalyticsSink({ track: (event, props) => gaEvent(event, props ?? {}) });
  } else {
    setAnalyticsSink({ track: () => {} });
    if (GA_ID) disableGA(GA_ID);
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [decision, setDecision] = useState<Decision | null>(null);
  const [bannerOpen, setBannerOpen] = useState(false);

  // Hydrate + apply on load.
  useEffect(() => {
    let saved: Decision | null = null;
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Decision;
        // Honour a stored choice only while it is still fresh (< ~24 months);
        // an expired or malformed timestamp re-opens the banner.
        const when = Date.parse(parsed.ts);
        if (Number.isFinite(when) && Date.now() - when < CONSENT_TTL_MS) {
          saved = parsed;
        }
      }
    } catch {
      /* ignore */
    }
    setDecision(saved);
    // Only prompt when analytics tooling is actually configured (otherwise there
    // are no non-essential cookies to consent to).
    setBannerOpen(!saved && !!GA_ID);
    if (saved) applyAnalytics(saved.analytics);
  }, []);

  const accept = useCallback((analytics: boolean) => {
    const next: Decision = { analytics, ts: new Date().toISOString() };
    setDecision(next);
    setBannerOpen(false);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* storage may be unavailable */
    }
    applyAnalytics(analytics);
  }, []);

  const reopen = useCallback(() => setBannerOpen(!!GA_ID), []);

  const value = useMemo<ConsentValue>(
    () => ({
      decided: decision !== null,
      analytics: decision?.analytics ?? false,
      bannerOpen,
      accept,
      reopen,
    }),
    [decision, bannerOpen, accept, reopen],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider');
  return ctx;
}

/** Whether a GA measurement id is configured at build time. */
export const GA_CONFIGURED = !!GA_ID;
